import { useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  MapPin,
  DollarSign,
  TrendingUp,
  Wallet,
  CreditCard,
  Banknote,
  HelpCircle,
  Target,
  User,
  Mail,
  Loader2,
  Shield,
  AlertCircle,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RED = "#D41F30";

/* ------------------------------------------------------------------ */
/*  STATIC OPTIONS                                                     */
/* ------------------------------------------------------------------ */

const TIMELINE_OPTIONS = [
  { value: "30 Days", label: "30 Days", sub: "Ready to buy now", emoji: "🗝️" },
  { value: "3 Months", label: "3 Months", sub: "Ready to start", emoji: "🏡" },
  { value: "6 Months", label: "6 Months", sub: "Ready soon", emoji: "📅" },
  { value: "1 Year", label: "1 Year", sub: "Starting the process", emoji: "🌱" },
];

const HOLDING_BACK_OPTIONS = [
  { value: "Financial limitations", emoji: "💰" },
  { value: "Market conditions", emoji: "📈" },
  { value: "Lack of knowledge", emoji: "🧭" },
  { value: "Lack of belief", emoji: "✨" },
];

const TOTAL_STEPS = 13;

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */

const fmtMoney = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

// Money formatter that appends "+" when the value is at (or above) the slider max
const fmtMoneyPlus = (n, max) => `${fmtMoney(n)}${n >= max ? "+" : ""}`;

/* ------------------------------------------------------------------ */
/*  PRIMITIVE UI PIECES                                                */
/* ------------------------------------------------------------------ */

const StepHeader = ({ stepNumber, Icon, title, subtitle }) => (
  <div className="mb-6 md:mb-10">
    <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-4 md:mb-6">
      <Icon className="w-5 h-5 md:w-7 md:h-7" style={{ color: RED }} />
    </div>
    <div
      className="text-xs font-bold tracking-[0.18em] mb-2 md:mb-3"
      style={{ color: RED }}
    >
      STEP {stepNumber}
    </div>
    <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] tracking-tight mb-3 md:mb-4">
      {title}
    </h2>
    {subtitle && (
      <p className="text-sm md:text-lg text-gray-500 leading-relaxed max-w-2xl">
        {subtitle}
      </p>
    )}
  </div>
);

const ChoiceCard = ({ active, onClick, label, sub, emoji, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`relative w-full text-left rounded-2xl border-2 p-4 md:p-6 transition-all duration-200 hover:shadow-lg ${
      active
        ? "border-[#D41F30] bg-red-50/60 shadow-md"
        : "border-gray-200 bg-white hover:border-gray-300"
    }`}
  >
    <div className="flex items-center gap-3 md:gap-4">
      {emoji && <span className="text-2xl md:text-3xl leading-none">{emoji}</span>}
      <div className="flex-1">
        <div className="text-lg md:text-xl font-bold text-gray-900">{label}</div>
        {sub && <div className="text-sm text-gray-500 mt-0.5">{sub}</div>}
      </div>
      {active && (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: RED }}
        >
          <Check className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
      )}
    </div>
  </button>
);

const PlainInput = ({ label, ...props }) => (
  <div>
    {label && (
      <label className="block text-base font-semibold text-gray-900 mb-2">
        {label}
      </label>
    )}
    <input
      {...props}
      className="w-full rounded-xl border-2 border-gray-200 bg-white px-5 py-4 text-lg text-gray-900 placeholder-gray-300 focus:border-[#D41F30] focus:outline-none focus:ring-0 transition-colors"
    />
  </div>
);

const RangeSlider = ({ min, max, step = 1, value, onChange, testid }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      data-testid={testid}
      className="cw-slider w-full h-2 rounded-full appearance-none cursor-pointer"
      style={{
        background: `linear-gradient(to right, ${RED} 0%, ${RED} ${pct}%, #FCE7E8 ${pct}%, #FCE7E8 100%)`,
      }}
    />
  );
};

const SliderBlock = ({ label, valueDisplay, min, max, step, value, onChange, minLabel, maxLabel, testid, footnote }) => (
  <div>
    <div className="flex items-end justify-between mb-4">
      <span className="text-base md:text-lg font-semibold text-gray-900">
        {label}
      </span>
      <span
        className="text-3xl md:text-4xl font-black tabular-nums"
        style={{ color: RED }}
        data-testid={`${testid}-value`}
      >
        {valueDisplay}
      </span>
    </div>
    <RangeSlider
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={onChange}
      testid={testid}
    />
    <div className="flex justify-between text-sm text-gray-400 mt-3">
      <span>{minLabel}</span>
      <span>{maxLabel}</span>
    </div>
    {footnote && (
      <p className="text-sm text-gray-500 mt-6">{footnote}</p>
    )}
  </div>
);

const MultiChoiceChip = ({ active, onClick, label, emoji, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-3 md:gap-4 rounded-2xl border-2 px-4 md:px-6 py-4 md:py-5 transition-all duration-200 hover:shadow-md text-left ${
      active
        ? "border-[#D41F30] bg-red-50/60 shadow-md"
        : "border-gray-200 bg-white hover:border-gray-300"
    }`}
  >
    <span className="text-xl md:text-2xl">{emoji}</span>
    <span className="flex-1 text-base md:text-lg font-semibold text-gray-900">{label}</span>
    {active && (
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center"
        style={{ backgroundColor: RED }}
      >
        <Check className="w-4 h-4 text-white" strokeWidth={3} />
      </div>
    )}
  </button>
);

/* ------------------------------------------------------------------ */
/*  MAIN COMPONENT                                                     */
/* ------------------------------------------------------------------ */

export default function LeadForm({ onSuccess }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    timeline: "",
    city: "",
    state: "",
    targetPrice: 390000,
    annualIncome: 130000,
    downPayment: 20000,
    creditScore: 680,
    veteranStatus: "",
    bankruptcy: "",
    monthlyDebt: 500,
    holdingBack: [],
    determination: 7,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    consent: false,
  });

  const update = (key, value) => setData((p) => ({ ...p, [key]: value }));

  const toggleHoldingBack = (val) =>
    setData((p) => ({
      ...p,
      holdingBack: p.holdingBack.includes(val)
        ? p.holdingBack.filter((v) => v !== val)
        : [...p.holdingBack, val],
    }));

  const canContinue = () => {
    switch (step) {
      case 1: return !!data.timeline;
      case 2: return data.city.trim().length > 1 && data.state.trim().length > 0;
      case 3: return data.targetPrice > 0;
      case 4: return data.annualIncome > 0;
      case 5: return data.downPayment >= 0;
      case 6: return data.creditScore >= 500;
      case 7: return !!data.veteranStatus;
      case 8: return !!data.bankruptcy;
      case 9: return data.monthlyDebt >= 0;
      case 10: return data.holdingBack.length > 0;
      case 11: return data.determination >= 1;
      case 12: return data.firstName.trim() && data.lastName.trim();
      case 13:
        return (
          /^\S+@\S+\.\S+$/.test(data.email.trim()) &&
          data.phone.replace(/\D/g, "").length >= 10 &&
          data.consent
        );
      default: return false;
    }
  };

  const next = () => {
    setError(null);
    if (step < TOTAL_STEPS) setStep(step + 1);
    else submit();
  };

  const back = () => {
    setError(null);
    if (step > 1) setStep(step - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/leads`, {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        email: data.email.trim(),
        phone: data.phone.trim(),
        timeline: data.timeline,
        city: data.city.trim(),
        state: data.state.trim(),
        location: `${data.city.trim()}, ${data.state.trim()}`,
        targetPrice: data.targetPrice,
        annualIncome: data.annualIncome,
        downPayment: data.downPayment,
        creditScore: data.creditScore,
        veteranStatus: data.veteranStatus,
        bankruptcy: data.bankruptcy,
        monthlyDebt: data.monthlyDebt,
        holdingBack: data.holdingBack,
        determination: data.determination,
        consent: data.consent,
      });
      if (res.data && res.data.syncStatus) {
        if (onSuccess) onSuccess(res.data);
        else window.location.assign("/homebuyer");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again or call us.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const pct = (step / TOTAL_STEPS) * 100;

  return (
    <div className="w-full" data-testid="lead-form-container">
      {/* Top progress bar */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center gap-6">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${pct}%`, backgroundColor: RED }}
              data-testid="lead-form-progress-bar"
            />
          </div>
          <div className="text-sm font-medium text-gray-500 tabular-nums">
            {step} of {TOTAL_STEPS}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-6 md:py-16">
        {step === 1 && (
          <div data-testid="lead-form-step-1">
            <StepHeader
              stepNumber={1}
              Icon={Sparkles}
              title="What's your homebuying plan?"
              subtitle="Pick the timeline that fits where you are right now. You can always adjust later."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TIMELINE_OPTIONS.map((opt) => (
                <ChoiceCard
                  key={opt.value}
                  active={data.timeline === opt.value}
                  onClick={() => update("timeline", opt.value)}
                  label={opt.label}
                  sub={opt.sub}
                  emoji={opt.emoji}
                  testid={`timeline-option-${opt.value.replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="lead-form-step-2">
            <StepHeader
              stepNumber={2}
              Icon={MapPin}
              title="Where do you want to live?"
              subtitle="Tell us the city and state you're looking at. Don't worry, you can be flexible."
            />
            <div className="space-y-5 max-w-xl">
              <PlainInput
                label="City"
                type="text"
                placeholder="Dallas"
                value={data.city}
                onChange={(e) => update("city", e.target.value)}
                data-testid="input-city"
                autoFocus
              />
              <PlainInput
                label="State"
                type="text"
                placeholder="TX"
                value={data.state}
                onChange={(e) => update("state", e.target.value)}
                data-testid="input-state"
                maxLength={50}
              />
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="lead-form-step-3">
            <StepHeader
              stepNumber={3}
              Icon={DollarSign}
              title="What's your target sales price?"
              subtitle="Drag to set the home price you're aiming for. We'll help you understand what's realistic."
            />
            <SliderBlock
              label="Target sales price"
              valueDisplay={fmtMoneyPlus(data.targetPrice, 2000000)}
              min={100000}
              max={2000000}
              step={10000}
              value={data.targetPrice}
              onChange={(v) => update("targetPrice", v)}
              minLabel="$100,000"
              maxLabel="$2,000,000+"
              testid="slider-target-price"
            />
          </div>
        )}

        {step === 4 && (
          <div data-testid="lead-form-step-4">
            <StepHeader
              stepNumber={4}
              Icon={TrendingUp}
              title="What's your annual income?"
              subtitle="This helps us calculate your buying power. Your information is private and secure."
            />
            <SliderBlock
              label="Annual household income"
              valueDisplay={fmtMoneyPlus(data.annualIncome, 1000000)}
              min={20000}
              max={1000000}
              step={5000}
              value={data.annualIncome}
              onChange={(v) => update("annualIncome", v)}
              minLabel="$20,000"
              maxLabel="$1,000,000+"
              testid="slider-annual-income"
            />
          </div>
        )}

        {step === 5 && (
          <div data-testid="lead-form-step-5">
            <StepHeader
              stepNumber={5}
              Icon={Wallet}
              title="How much do you have for a down payment?"
              subtitle="Include personal savings, retirement funds, and any gifts from family. The total amount available."
            />
            <SliderBlock
              label="Down payment available"
              valueDisplay={fmtMoneyPlus(data.downPayment, 500000)}
              min={0}
              max={500000}
              step={1000}
              value={data.downPayment}
              onChange={(v) => update("downPayment", v)}
              minLabel="$0"
              maxLabel="$500,000+"
              testid="slider-down-payment"
              footnote="No down payment? No problem, Veterans qualify for $0 down and we offer programs like DubbleUpp."
            />
          </div>
        )}

        {step === 6 && (
          <div data-testid="lead-form-step-6">
            <StepHeader
              stepNumber={6}
              Icon={CreditCard}
              title="What's your approximate credit score?"
              subtitle="Don't worry if it's not perfect. We work with scores as low as 580."
            />
            <SliderBlock
              label="Credit score"
              valueDisplay={data.creditScore}
              min={500}
              max={850}
              step={1}
              value={data.creditScore}
              onChange={(v) => update("creditScore", v)}
              minLabel="500"
              maxLabel="850"
              testid="slider-credit-score"
              footnote={
                data.creditScore >= 760
                  ? "Excellent, you'll qualify for the best rates."
                  : data.creditScore >= 700
                  ? "Very good, strong loan options available."
                  : data.creditScore >= 640
                  ? "Good, plenty of programs to choose from."
                  : data.creditScore >= 580
                  ? "We can absolutely work with this. Many of our buyers start here."
                  : "Below 580, we'll help you build a path forward. Let's talk."
              }
            />
          </div>
        )}

        {step === 7 && (
          <div data-testid="lead-form-step-7">
            <StepHeader
              stepNumber={7}
              Icon={Shield}
              title="Are you a veteran, active military, or surviving spouse?"
              subtitle="Veterans and active service members qualify for VA loans | $0 down, no mortgage insurance."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              {["Yes", "No"].map((opt) => (
                <ChoiceCard
                  key={opt}
                  active={data.veteranStatus === opt}
                  onClick={() => update("veteranStatus", opt)}
                  label={opt}
                  testid={`veteran-option-${opt}`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 8 && (
          <div data-testid="lead-form-step-8">
            <StepHeader
              stepNumber={8}
              Icon={AlertCircle}
              title="Have you declared bankruptcy in the past 3 years?"
              subtitle="Just being honest, we can still build a path forward either way."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              {["Yes", "No"].map((opt) => (
                <ChoiceCard
                  key={opt}
                  active={data.bankruptcy === opt}
                  onClick={() => update("bankruptcy", opt)}
                  label={opt}
                  testid={`bankruptcy-option-${opt}`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 9 && (
          <div data-testid="lead-form-step-9">
            <StepHeader
              stepNumber={9}
              Icon={Banknote}
              title="What's your monthly debt?"
              subtitle="Include car payments, personal loans, credit cards, and student loans. Your monthly minimums."
            />
            <SliderBlock
              label="Total monthly debt payments"
              valueDisplay={fmtMoney(data.monthlyDebt)}
              min={0}
              max={5000}
              step={50}
              value={data.monthlyDebt}
              onChange={(v) => update("monthlyDebt", v)}
              minLabel="$0"
              maxLabel="$5,000"
              testid="slider-monthly-debt"
            />
          </div>
        )}

        {step === 10 && (
          <div data-testid="lead-form-step-10">
            <StepHeader
              stepNumber={10}
              Icon={HelpCircle}
              title="What's been holding you back?"
              subtitle="Be honest, this helps us craft a plan that actually solves the right problem. Pick all that apply."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {HOLDING_BACK_OPTIONS.map((opt) => (
                <MultiChoiceChip
                  key={opt.value}
                  active={data.holdingBack.includes(opt.value)}
                  onClick={() => toggleHoldingBack(opt.value)}
                  label={opt.value}
                  emoji={opt.emoji}
                  testid={`holding-option-${opt.value.replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 11 && (
          <div data-testid="lead-form-step-11">
            <StepHeader
              stepNumber={11}
              Icon={Target}
              title="How determined are you?"
              subtitle="On a scale of 1-10, how committed are you to becoming a homeowner in the next year?"
            />
            <div>
              <div className="flex items-end justify-between mb-4">
                <span className="text-base md:text-lg font-semibold text-gray-900">
                  Determination level
                </span>
                <span
                  className="text-3xl md:text-4xl font-black tabular-nums"
                  style={{ color: RED }}
                  data-testid="slider-determination-value"
                >
                  {data.determination} / 10
                </span>
              </div>
              <RangeSlider
                min={1}
                max={10}
                step={1}
                value={data.determination}
                onChange={(v) => update("determination", v)}
                testid="slider-determination"
              />
              <div className="grid grid-cols-3 text-sm text-gray-400 mt-3">
                <span className="text-left">1 / 10<br /><span className="text-gray-500">Just curious</span></span>
                <span className="text-center">5 / 10<br /><span className="text-gray-500">Thinking about it</span></span>
                <span className="text-right">10 / 10<br /><span className="text-gray-500">All in</span></span>
              </div>
            </div>
          </div>
        )}

        {step === 12 && (
          <div data-testid="lead-form-step-12">
            <StepHeader
              stepNumber={12}
              Icon={User}
              title="What's your name?"
              subtitle="So we know how to introduce ourselves."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-xl">
              <PlainInput
                label="First name"
                type="text"
                placeholder="Jane"
                value={data.firstName}
                onChange={(e) => update("firstName", e.target.value)}
                data-testid="input-firstName"
                autoFocus
              />
              <PlainInput
                label="Last name"
                type="text"
                placeholder="Doe"
                value={data.lastName}
                onChange={(e) => update("lastName", e.target.value)}
                data-testid="input-lastName"
              />
            </div>
          </div>
        )}

        {step === 13 && (
          <div data-testid="lead-form-step-13">
            <StepHeader
              stepNumber={13}
              Icon={Mail}
              title={`How do we reach you, ${data.firstName || "friend"}?`}
              subtitle="We'll build a personalized homebuying plan for you and have our homebuying team reach out to you!"
            />
            <div className="space-y-5 max-w-xl">
              <PlainInput
                label="Email address"
                type="email"
                inputMode="email"
                placeholder="jane@example.com"
                value={data.email}
                onChange={(e) => update("email", e.target.value)}
                data-testid="input-email"
                autoFocus
              />
              <PlainInput
                label="Phone number"
                type="tel"
                inputMode="tel"
                placeholder="(555) 555-1234"
                value={data.phone}
                onChange={(e) => update("phone", e.target.value)}
                data-testid="input-phone"
              />
              <label className="flex items-start gap-3 mt-6 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={data.consent}
                  onChange={(e) => update("consent", e.target.checked)}
                  data-testid="input-consent"
                  className="mt-1 w-5 h-5 accent-[#D41F30] cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-gray-600 leading-relaxed">
                  I agree to receive a phone call, email and SMS messages from
                  CityWorth Mortgage and CityWorth Homes regarding my homebuying plan. I understand
                  consent isn&apos;t required to purchase.
                </span>
              </label>
            </div>
          </div>
        )}

        {error && (
          <div
            className="mt-8 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
            data-testid="lead-form-error"
          >
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 md:mt-12">
          {step > 1 ? (
            <button
              type="button"
              onClick={back}
              disabled={submitting}
              className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium text-base px-2 py-2 transition-colors disabled:opacity-50"
              data-testid="lead-form-back"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          ) : (
            <span />
          )}
          <button
            type="button"
            onClick={next}
            disabled={!canContinue() || submitting}
            className="inline-flex items-center gap-2 text-white font-semibold text-base rounded-xl px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-md"
            style={{ backgroundColor: RED }}
            data-testid="lead-form-next"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Sending...
              </>
            ) : step === TOTAL_STEPS ? (
              <>
                Get my homebuying plan
                <ArrowRight className="w-5 h-5" />
              </>
            ) : (
              <>
                Continue
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
